import { Expander } from '@postidigital/posti-components'
import { observer } from 'mobx-react-lite'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { StoreContext } from '../../../store'
import { DropDownType, IOrganizationItem } from '../../../store/dataModels/interfaces'
import { ContentSection } from '../../../style/layout'
import { addValuesToLabels } from '../../../utils/helpers'
import { ExpanderContractNumberList, MainUserRadioGroup, SelectRoles, SubTitle } from '../..'
import { EditRemove } from './EditDeleteComponent'

interface IProps {
  organization: IOrganizationItem
  selectedRoleValues: string[]
  toggleUserRole?: (role: string) => any
  selectedCustomerNumbers?: string[]
  selectedLCNumbers?: string[]
  selectedTransportIds?: string[]
  selectedBusinessPartnerNumbers?: string[]
  selectedPrincipalIds?: string[]
  editable?: boolean
  accountId?: string
  userEmail?: string
  isOpened?: boolean
}

export const OrganisationExpanderComponent: React.FC<IProps> = ({
  organization,
  organization: {
    name,
    businessId,
    customerNumbers,
    logisticsContractNumbers,
    transportIds,
    businessPartnerNumbers,
    principalIds,
  },
  selectedRoleValues,
  toggleUserRole,
  selectedCustomerNumbers,
  selectedLCNumbers,
  selectedTransportIds,
  selectedBusinessPartnerNumbers,
  selectedPrincipalIds,
  editable,
  accountId,
  userEmail,
  isOpened = false,
}) => {
  const { layoutStore } = useContext(StoreContext)
  const [isExpanded, setIsExpanded] = useState(isOpened)
  const { t } = useTranslation()

  const userCustomerNumbers = customerNumbers.filter((c) => selectedCustomerNumbers.includes(c.value))
  const userLCNumbers = logisticsContractNumbers.filter((c) => selectedLCNumbers.includes(c.value))
  const userTransportIds = transportIds.filter((c) => selectedTransportIds.includes(c.value))
  const userBusinessPartnerNumbers = businessPartnerNumbers.filter((c) =>
    selectedBusinessPartnerNumbers.includes(c.value)
  )
  const userPrincipalIds = principalIds.filter((c) => selectedPrincipalIds.includes(c.value))

  return (
    <StyledExpander id="content" open={isExpanded} hasShadow hasRoundedCorners style={{ flexGrow: 1 }}>
      <Expander.Title
        style={{ padding: '1rem' }}
        aria-controls="content"
        aria-expanded={isExpanded}
        onClick={() => {
          setIsExpanded(!isExpanded)
        }}
      >
        <SubTitle as="div">{`${name} (${businessId})`}</SubTitle>
      </Expander.Title>
      <Expander.Body>
        <ContentSection top="md" style={{ background: 'white', padding: '8px' }}>
          {editable && layoutStore.isMobile && (
            <EditRemove businessId={organization.businessId} accountId={accountId} email={userEmail} inline />
          )}
          <MainUserRadioGroup
            disabled
            isMainUser={selectedRoleValues.includes('MainUser')}
            toggleUserRole={() => toggleUserRole('MainUser')}
          />
          <SelectRoles
            roleGroups={organization.roleGroups}
            selectedRoleValues={selectedRoleValues}
            disabled
            infoTranslationID="general.roleRequirementInfo2"
          />
          {customerNumbers && (
            <ExpanderContractNumberList
              title={t(`general.customerNumbers`)}
              options={addValuesToLabels(userBusinessPartnerNumbers, DropDownType.BUSINESS_PARTNER_NUMBER).concat(
                addValuesToLabels(userCustomerNumbers, DropDownType.CUSTOMER_NUMBER)
              )}
            />
          )}
          {logisticsContractNumbers && (
            <ExpanderContractNumberList
              title={t(`general.LCSNumbers`)}
              options={addValuesToLabels(userLCNumbers, DropDownType.LOGISTICS_CONTRACT_NUMBER)}
            />
          )}
          {transportIds && (
            <ExpanderContractNumberList
              title={t(`general.transportIds`)}
              options={addValuesToLabels(userTransportIds, DropDownType.TRANSPORT_ID)}
            />
          )}
          {principalIds && (
            <ExpanderContractNumberList
              title={t(`general.principalIds`)}
              options={addValuesToLabels(userPrincipalIds, DropDownType.PRINCIPAL_ID)}
            />
          )}
        </ContentSection>
      </Expander.Body>
    </StyledExpander>
  )
}

export const OrganisationExpander = observer(OrganisationExpanderComponent)

const StyledExpander = styled(Expander)(
  ({ theme }) => css`
    > button:focus {
      outline: none;
      border-color: transparent;
    }

    > button:focus-visible {
      box-shadow: ${theme.color.foreground.accent.lowContrast.default} 0 0 0 ${theme.spacing.space1}rem inset;
    }

    > button:focus-visible::before {
      content: none;
    }
  `
)
